import {
  Clear as ClearIcon,
  Lightbulb,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Modal,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { KnowledgeApi } from "../../functions/api/knowledge";
import type { Knowledge } from "../../interface/knowledge";
import type { RootState } from "../../store";

interface EnhancedReminiscenceModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (knowledges: Knowledge[]) => void;
}

/**
 * 【デバウンス機能】: 検索入力の連続API呼び出しを制限
 * 【機能概要】: 指定した遅延時間後に関数を実行し、連続呼び出しを防ぐ
 * 【パフォーマンス】: API呼び出し回数を削減し、サーバー負荷を軽減
 * 🟢 信頼性レベル: 一般的なデバウンスパターンの実装
 */
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const EnhancedReminiscenceModal: React.FC<EnhancedReminiscenceModalProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  // =============================================================================
  // Redux状態とAPI初期化
  // =============================================================================

  // TODO: These will be used when fetchReminiscences implementation is uncommented
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { currentProject: _currentProject } = useSelector((state: RootState) => state.auth);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // @ts-expect-error Reserved for future implementation
  const _knowledgeApi = useMemo(() => new KnowledgeApi(), []);

  // =============================================================================
  // ローカル状態管理
  // =============================================================================

  // データ状態
  const [reminiscences] = useState<Knowledge[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  // ページング状態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages] = useState(0);
  const [totalItems] = useState(0);

  // 検索状態
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);

  // UI状態
  const [isLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // =============================================================================
  // データ取得処理
  // =============================================================================

  /**
   * 【データ取得】: system-proposalレミニセンスの取得
   * 【機能概要】: ページング・検索パラメータに基づいてAPIを呼び出し
   * 【エラーハンドリング】: 通信エラー・データエラーの適切な処理
   * 🟢 信頼性レベル: APIレスポンス検証とエラーハンドリング完備
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fetchReminiscences = useCallback(async (_page: number = 1, _search: string = "") => {
    // TODO: Implementation is commented out for future use
    // When implementing, add proper dependencies: [currentProject, knowledgeApi, debouncedSearch]
  }, []);

  // =============================================================================
  // エフェクト処理
  // =============================================================================

  /**
   * 【初期データ取得】: モーダル開封時の初期ロード
   * 【依存関係】: モーダル開封状態に依存
   * 🟢 信頼性レベル: 標準的なReactパターン
   */
  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      fetchReminiscences(1, debouncedSearch);
    } else {
      // 【クリーンアップ】: モーダル閉じ時の状態リセット
      setSearchInput("");
      setSelectedItems([]);
      setError(null);
    }
  }, [open, fetchReminiscences, debouncedSearch]);

  /**
   * 【検索機能】: デバウンス済み検索文字列による再取得
   * 【機能概要】: 300ms遅延後に検索実行、ページを1にリセット
   * 🟢 信頼性レベル: デバウンス処理による最適化実装
   */
  useEffect(() => {
    if (open && debouncedSearch !== searchInput) {
      setCurrentPage(1);
      fetchReminiscences(1, debouncedSearch);
    }
  }, [debouncedSearch, open, fetchReminiscences, searchInput]);

  // =============================================================================
  // イベントハンドラー
  // =============================================================================

  /**
   * 【選択切り替え】: レミニセンス項目の選択状態切り替え
   * 【機能概要】: 複数選択対応、選択状態のトグル処理
   * 🟢 信頼性レベル: 標準的な選択UI実装
   */
  const handleToggle = useCallback((id: string) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }, []);

  /**
   * 【ページ変更】: ページネーション操作の処理
   * 【機能概要】: 指定ページのデータ取得、選択状態のリセット
   * 🟢 信頼性レベル: ページング処理の標準実装
   */
  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => {
      setCurrentPage(page);
      fetchReminiscences(page, debouncedSearch);
    },
    [fetchReminiscences, debouncedSearch],
  );

  /**
   * 【検索クリア】: 検索文字列のクリアと初期状態復帰
   * 【機能概要】: 検索条件をクリアして最初のページに戻る
   * 🟢 信頼性レベル: 標準的なフォームリセット処理
   */
  const handleClearSearch = useCallback(() => {
    setSearchInput("");
    setCurrentPage(1);
    fetchReminiscences(1, "");
  }, [fetchReminiscences]);

  /**
   * 【再読み込み】: 現在の条件でデータの再取得
   * 【機能概要】: 同じ検索・ページ条件での強制再読み込み
   * 🟢 信頼性レベル: 標準的なリフレッシュ処理
   */
  const handleRefresh = useCallback(() => {
    fetchReminiscences(currentPage, debouncedSearch);
  }, [fetchReminiscences, currentPage, debouncedSearch]);

  /**
   * 【選択確定】: 選択されたレミニセンスを親コンポーネントに返す
   * 【機能概要】: 選択IDから実際のオブジェクトを取得して返却
   * 🟢 信頼性レベル: データ整合性確保済み
   */
  const handleConfirm = useCallback(() => {
    const selected = reminiscences.filter((r) => selectedItems.includes(r.id));
    onSelect(selected);
    onClose();
    setSelectedItems([]);
  }, [reminiscences, selectedItems, onSelect, onClose]);

  // =============================================================================
  // UI定義
  // =============================================================================

  const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 700,
    bgcolor: "background.paper",
    borderRadius: "8px",
    boxShadow: 24,
    p: 0,
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* ヘッダーエリア */}
        <Box sx={{ p: 3, borderBottom: "1px solid #e5e3dc" }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            レミニセンスを選択 (System Proposal)
          </Typography>

          {/* 検索バー */}
          <TextField
            fullWidth
            size="small"
            placeholder="レミニセンスをタイトルで検索..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} aria-label="検索をクリア">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* 統計情報 */}
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {totalItems > 0
                ? `${Math.min((currentPage - 1) * 10 + 1, totalItems)}-${Math.min(currentPage * 10, totalItems)} / ${totalItems}件`
                : "0件"}
              {debouncedSearch && ` (検索: "${debouncedSearch}")`}
            </Typography>

            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isLoading}
              aria-label="再読み込み"
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* メインコンテンツエリア */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2, minHeight: 300 }}>
          {/* エラー表示 */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleRefresh}>
                  再試行
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {/* ローディング表示 */}
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 1 }}>
                読み込み中...
              </Typography>
            </Box>
          )}

          {/* データなし表示 */}
          {!isLoading && !error && reminiscences.length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Lightbulb sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {debouncedSearch
                  ? `"${debouncedSearch}" に該当するレミニセンスが見つかりませんでした`
                  : "System Proposalのレミニセンスがありません"}
              </Typography>
              {debouncedSearch && (
                <Button variant="outlined" size="small" onClick={handleClearSearch} sx={{ mt: 1 }}>
                  検索をクリア
                </Button>
              )}
            </Box>
          )}

          {/* レミニセンスリスト */}
          {!isLoading && reminiscences.length > 0 && (
            <List>
              {reminiscences.map((rem) => (
                <ListItem key={rem.id} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleToggle(rem.id)}
                    selected={selectedItems.includes(rem.id)}
                    sx={{
                      border: "1px solid #e5e3dc",
                      borderRadius: "4px",
                      "&.Mui-selected": {
                        backgroundColor: "#f0f9ff",
                        borderColor: "#1d9fd6",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
                      <Lightbulb sx={{ color: "#1d9fd6", mt: 0.5 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {rem.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            "-webkit-line-clamp": 2,
                            "-webkit-box-orient": "vertical",
                          }}
                        >
                          {rem.meta_prompt}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                          <Chip
                            label={rem.status}
                            size="small"
                            color={rem.status === "approved" ? "success" : "default"}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(rem.created_at).toLocaleDateString("ja-JP")}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {/* ページネーション */}
          {!isLoading && totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>

        {/* フッターエリア */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e5e3dc",
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
          }}
        >
          <Button onClick={onClose} variant="outlined">
            キャンセル
          </Button>
          <Button onClick={handleConfirm} variant="contained" disabled={selectedItems.length === 0}>
            選択 ({selectedItems.length})
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EnhancedReminiscenceModal;
